import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-qtl-param-idx", "onMouseover", "onMouseleave"]
const _hoisted_2 = { class: "qtl-param-row__index" }
const _hoisted_3 = {
  key: 1,
  class: "qtl-param-row__placeholder"
}

import { computed, ref, watch } from "vue";
import type {
  CellDataParameter,
  TableDataCell,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import SimpleCellElementLite from "./SimpleCellElementLite.vue";

// сентинел для подсчёта групп: заведомо не равен ни одному ParentRecordId
interface ParamRenderRow {
  elem: any;
  slotStart: number;
  span: number;
  spanIndices: number[];
  isGroupStart: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ParameterCellElementLite',
  props: {
    element: {},
    tableRowNumber: {},
    columnIndex: {},
    hoveredParamRecId: {},
    noRowHeightAlign: { type: Boolean }
  },
  emits: ["changeHoverRecord", "changeHeights"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const NO_GROUP = Symbol("no-group");

const props = __props;

const emit = __emit;

/** Параметры (sub-rows) для рендера. Один-в-один с ParameterCellElement.params,
 *  но с пробросом __placeholder из useTableModel-нормализации. */
const params = computed<any[]>(() =>
  (props.element.Data || []).map((el) => ({
    ...props.element,
    ParentRecordId: (el as CellDataParameter).ParentRecordId,
    Data: [el],
    __placeholder: !!(el as any).__placeholder,
  })),
);

/** Map<ParentRecordId, index> для отображения "1.", "2." перед группами параметров.
 *  Placeholder'ы участвуют наравне (у них может быть тот же ParentRecordId,
 * но рендерим без префикса, он всё равно повторяется). */
const indicesArray = computed<Map<unknown, number>>(() => {
  const map = new Map<unknown, number>();
  let pIndex = 0;
  let oldP: unknown = undefined;
  params.value.forEach((el) => {
    if (el.ParentRecordId !== oldP) {
      pIndex++;
      oldP = el.ParentRecordId;
    }
    map.set(el.ParentRecordId, pIndex);
  });
  return map;
});

// Сколько различных групп (ParentRecordId) в ячейке. Нумерация "1.", "2." нужна
// только когда групп больше одной, чтобы связать подстроки между колонками. При
// единственной группе (одно значение или несколько значений одного ParentRecordId)
// префикс "1." - просто шум, поэтому его прячем.
const groupCount = computed<number>(() => {
  let count = 0;
  // сентинел заведомо не равен ни одному реальному ParentRecordId (включая undefined),
  // чтобы первая группа всегда засчитывалась
  let oldP: unknown = NO_GROUP;
  for (const el of params.value) {
    if (el.ParentRecordId !== oldP) {
      count++;
      oldP = el.ParentRecordId;
    }
  }
  return count;
});

/**
 * Строки для рендера. Обычно один слот = одна подстрока. Но если в подгруппе
 * (последовательные слоты с одним ParentRecordId) у ЭТОЙ колонки ровно одно
 * значение, а сама группа занимает несколько слотов (в соседних колонках у той же
 * записи несколько значений) - схлопываем группу в ОДНУ подстроку, растянутую на
 * высоту всех слотов группы. Так одиночное «объединяющее» значение (например,
 * сумма) визуально накрывает весь блок своей подгруппы, а не висит в первом слоте.
 *
 * span > 1 → подстрока покрывает несколько слотов; её min-height считаем как сумму
 * высот этих слотов + внутренние отступы. Такую подстроку НЕ помечаем
 * data-qtl-param-idx: высоты слотов берутся из «многострочных» колонок (например,
 * названий), а растянутое значение лишь тянется к их сумме.
 */
const renderRows = computed<ParamRenderRow[]>(() => {
  const p = params.value;
  const rows: ParamRenderRow[] = [];
  const disableSpan = !!props.noRowHeightAlign;
  let i = 0;
  while (i < p.length) {
    const gid = p[i].ParentRecordId;
    const canGroup = gid !== undefined && gid !== null && gid !== "";
    // границы подгруппы: подряд идущие слоты с одним ParentRecordId
    let j = i;
    if (canGroup) {
      while (j + 1 < p.length && p[j + 1].ParentRecordId === gid) j++;
    }
    const slotIndices: number[] = [];
    for (let k = i; k <= j; k++) slotIndices.push(k);
    const reals = slotIndices.filter((k) => !p[k].__placeholder);
    if (!disableSpan && canGroup && slotIndices.length > 1 && reals.length === 1) {
      rows.push({
        elem: p[reals[0]],
        slotStart: i,
        span: slotIndices.length,
        spanIndices: slotIndices,
        isGroupStart: true,
      });
    } else {
      slotIndices.forEach((k, idx) => {
        rows.push({
          elem: p[k],
          slotStart: k,
          span: 1,
          spanIndices: [k],
          isGroupStart: idx === 0,
        });
      });
    }
    i = j + 1;
  }
  return rows;
});

function rowIndexPrefix(row: ParamRenderRow): string {
  if (row.elem.__placeholder) return "";
  if (groupCount.value <= 1) return "";
  if (!row.isGroupStart) return "";
  const idx = indicesArray.value.get(row.elem.ParentRecordId);
  if (!idx || !row.elem.ParentRecordId) return "";
  return idx + ". ";
}

// min-height подстроки. Обычная подстрока тянется к высоте своего слота, растянутая
// (span > 1) - к сумме высот покрытых слотов плюс внутренние 3px-отступы между ними
// (см. margin-bottom .qtl-param-row), чтобы точно совпасть с высотой блока подгруппы
// в соседних колонках.
function rowMinHeightStyle(row: ParamRenderRow): Record<string, string> | undefined {
  if (props.noRowHeightAlign) return undefined;
  if (row.span <= 1) {
    return { minHeight: `var(--qtl-psub-${row.slotStart}-h, 0)` };
  }
  const parts = row.spanIndices.map((idx) => `var(--qtl-psub-${idx}-h, 0)`);
  const gapPx = (row.span - 1) * 3;
  return { minHeight: `calc(${parts.join(" + ")} + ${gapPx}px)` };
}

// Локальный hover-state. Тип сознательно широкий (string | number | undefined),
// см. комментарий к prop hoveredParamRecId выше, ParentRecordId может быть как
// строкой, так и числом в зависимости от источника данных.
const hoveredParentRecId = ref<string | number | undefined>(undefined);

watch(hoveredParentRecId, (v) => {
  emit("changeHoverRecord", { recId: v, rowNum: props.tableRowNumber ?? 0 });
});

// Когда сверху прилетает новое значение hoveredParamRecId, логируем,
// сколько подстрок в данной ячейке имеют совпадающий ParentRecordId.
// Если 0 при ненулевом target, подсветки не будет, виден реальный
// массив доступных ParentRecordId этой ячейки → видно, расходятся ли
// типы (string/number) и есть ли вообще ParentRecordId в данных.
watch(() => props.hoveredParamRecId, (target) => {
  if (!(typeof window !== "undefined") || !(window as any).__qtlDebugHighlight) return;
  if (target === undefined || target === null || target === "" || target === 0) return;
  const recIds = params.value.map((p) => p.ParentRecordId);
  const matches = recIds.filter((r) => r !== undefined && r !== null && String(r) === String(target));
  // eslint-disable-next-line no-console
  console.debug("[QTL highlight] param.target-changed", {
    column: props.columnIndex,
    target,
    typeofTarget: typeof target,
    paramsCount: params.value.length,
    paramsRecIds: recIds,
    matches: matches.length,
  });
});

function onMouseover(arg: string | number | undefined) {
  hoveredParentRecId.value = arg;
  if ((typeof window !== "undefined") && (window as any).__qtlDebugHighlight) {
    // eslint-disable-next-line no-console
    console.debug("[QTL highlight] param.onMouseover", {
      column: props.columnIndex,
      recId: arg,
      typeofRecId: typeof arg,
      paramsCount: params.value.length,
      paramsRecIds: params.value.map((p) => p.ParentRecordId),
    });
  }
}

function onMouseleave(arg: string | number | undefined) {
  // Сравнение строгое, но мы НЕ конвертируем типы, потому что arg всегда
  // совпадает по типу с тем, что положили в onMouseover (одно и то же поле
  // из одного источника). Сброс делаем в undefined → корректно гасит подсветку.
  if (hoveredParentRecId.value === arg) hoveredParentRecId.value = undefined;
}

/**
 * Подсветка строки (match-rec из оригинала). Источник истины, prop hoveredParamRecId,
 * который выставляется на уровне QTLiteRow при hover на ЛЮБОЙ Parameter-ячейке
 * этой строки. Это даёт cross-cell highlight как в QueryTable.onRecordHover.
 *
 * Сравнение через String(), потому что ParentRecordId объявлен как string в моделях,
 * но в реальных данных может приходить и как number. Без принуждения было бы
 * `'5' === 5` → false, и подсветка просто не появлялась.
 */
function isMatch(elem: any): boolean {
  if (elem.__placeholder) return false;
  const target = props.hoveredParamRecId;
  if (target === undefined || target === null || target === "" || target === 0) return false;
  if (elem.ParentRecordId === undefined || elem.ParentRecordId === null) return false;
  const eq = String(elem.ParentRecordId) === String(target);
  if (eq && (typeof window !== "undefined") && (window as any).__qtlDebugHighlight) {
    // eslint-disable-next-line no-console
    console.debug("[QTL highlight] param.isMatch HIT", {
      column: props.columnIndex,
      target,
      elemRecId: elem.ParentRecordId,
    });
  }
  return eq;
}

/**
 * QueryTable вызывал setCompareRecord через ref-карту parameter_cells.
 * Здесь оставлены заглушки, чтобы внешние вызовы не падали.
 * updateHeights/launchRecalcHeights делает useRowParameterHeights + CSS-переменные.
 */
function setCompareRecord(_val: number) {}
function updateHeights(_h: any) { /* handled by row-level CSS vars */ }
function launchRecalcHeights() { /* handled by row-level CSS vars */ }

__expose({ setCompareRecord, updateHeights, launchRecalcHeights });

return (_ctx: any,_cache: any) => {
  return (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(renderRows.value, (row, rIdx) => {
    return (_openBlock(), _createElementBlock("div", {
      key: rIdx,
      class: _normalizeClass(["qtl-param-row", {
      'qtl-param-row--match': isMatch(row.elem),
      'qtl-param-row--empty': !!row.elem.__placeholder,
    }]),
      "data-qtl-param-idx": (_ctx.noRowHeightAlign || row.span > 1) ? undefined : row.slotStart,
      style: _normalizeStyle(rowMinHeightStyle(row)),
      onMouseover: ($event: any) => (!row.elem.__placeholder && onMouseover(row.elem.ParentRecordId)),
      onMouseleave: ($event: any) => (!row.elem.__placeholder && onMouseleave(row.elem.ParentRecordId))
    }, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(rowIndexPrefix(row)), 1),
      (!row.elem.__placeholder)
        ? (_openBlock(), _createBlock(SimpleCellElementLite, {
            key: 0,
            element: row.elem
          }, null, 8, ["element"]))
        : (_openBlock(), _createElementBlock("span", _hoisted_3, " "))
    ], 46, _hoisted_1))
  }), 128))
}
}

})